import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  PoundSterling,
  User,
  FileText,
  MessageSquare,
  Save,
  X,
  Search,
  CheckCircle2,
  Building2,
  Folder,
  Activity,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { TransactionTypeBadge } from '@/components/transactions/transaction-type-badge';
import { getContactsByCustomerId } from '@/lib/demo-data/customer-communications-contacts';
import type { ContactApi } from '@/types/api/customer-detail';
import { formatCurrency } from '@/lib/utils/formatters';

interface CreatePromiseDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: PromiseFormData) => void;
  prePopulatedCustomer?: {
    customerId: string;
    customerName: string;
    accountNumber: string;
  };
  prePopulatedTransactions?: MockInvoice[];
}

// Internal form state type
interface PromiseFormState {
  // Customer Information
  customerId: string;
  customerName: string;
  customerAccountNumber: string;
  contactId: string;
  contactName: string;
  customerEmail: string;
  customerPhone: string;
  
  // Promise Details
  promiseDate: Date | undefined;
  promisedAmount: string;
  confirmationMethod: string;
  caseId?: string;  // Optional link to collection case
  
  // Invoice Selection
  selectedInvoices: Set<string>;
  invoiceAllocations: Record<string, number>;
  
  // Notes
  notes: string;
  sendConfirmation: boolean;
}

// Submit payload type for onSave callback
export interface PromiseFormData {
  customer: {
    id: string;
    name: string;
    accountNumber: string;
    email: string;
    phone: string;
  };
  promiseDate: Date;
  promisedAmount: number;
  confirmationMethod: string;
  selectedInvoices: string[];
  invoiceAllocations: Record<string, number>;
  notes: string;
  sendConfirmation: boolean;
}

interface MockInvoice {
  id: string;
  headerReference: string;
  invoiceDate: Date;
  originalAmount: number;
  balanceRemaining: number;
  daysOverdue: number;
}

// Mock customer data for demonstration
const mockCustomers = [
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    email: 'accounts@acme.com',
    phone: '+1 (555) 123-4567',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    accountNumber: 'ACC-002',
    email: 'finance@globalind.com',
    phone: '+1 (555) 234-5678',
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    accountNumber: 'ACC-003',
    email: 'billing@techsol.com',
    phone: '+1 (555) 345-6789',
  },
];

// Mock invoices for demonstration
const mockInvoices: MockInvoice[] = [
  {
    id: 'INV-001',
    headerReference: 'INV-2024-001',
    invoiceDate: new Date(2024, 0, 15),
    originalAmount: 5000,
    balanceRemaining: 5000,
    daysOverdue: 45,
  },
  {
    id: 'INV-002',
    headerReference: 'INV-2024-002',
    invoiceDate: new Date(2024, 1, 1),
    originalAmount: 3500,
    balanceRemaining: 3500,
    daysOverdue: 30,
  },
  {
    id: 'INV-003',
    headerReference: 'INV-2024-003',
    invoiceDate: new Date(2024, 1, 15),
    originalAmount: 2000,
    balanceRemaining: 2000,
    daysOverdue: 15,
  },
];

// Mock collection cases for demonstration
const mockCases = [
  { id: 'case-1', caseNumber: 'AR-00001', status: 'Active', priority: 'High' },
  { id: 'case-2', caseNumber: 'AR-00002', status: 'Active', priority: 'Medium' },
  { id: 'case-3', caseNumber: 'AR-00003', status: 'Active', priority: 'Low' },
  { id: 'case-4', caseNumber: 'AR-00004', status: 'On Hold', priority: 'Medium' },
];

export const CreatePromiseDrawer: React.FC<CreatePromiseDrawerProps> = ({ open, onOpenChange, onSave, prePopulatedCustomer, prePopulatedTransactions }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCustomer, setSelectedCustomer] = useState<typeof mockCustomers[0] | null>(null);
  const [availableContacts, setAvailableContacts] = useState<ContactApi[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactApi | null>(null);
  
  const [formData, setFormData] = useState<PromiseFormState>({
    customerId: '',
    customerName: '',
    customerAccountNumber: '',
    contactId: '',
    contactName: '',
    customerEmail: '',
    customerPhone: '',
    promiseDate: undefined,
    promisedAmount: '',
    confirmationMethod: '',
    selectedInvoices: new Set(),
    invoiceAllocations: {},
    notes: '',
    sendConfirmation: true,
  });

  // Use pre-populated transactions if provided, otherwise use mock data
  const availableInvoices = prePopulatedTransactions || mockInvoices;

  // Load contacts when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      const contacts = getContactsByCustomerId(selectedCustomer.id);
      setAvailableContacts(contacts);
      
      // Auto-select primary contact if available
      const primaryContact = contacts.find(c => c.isPrimary);
      if (primaryContact) {
        setSelectedContact(primaryContact);
        setFormData(prev => ({
          ...prev,
          contactId: primaryContact.id,
          contactName: `${primaryContact.firstName} ${primaryContact.surname}`,
          customerEmail: primaryContact.email,
          customerPhone: primaryContact.phone,
        }));
      }
    } else {
      setAvailableContacts([]);
      setSelectedContact(null);
    }
  }, [selectedCustomer]);

  // Handle pre-populated customer when drawer opens
  useEffect(() => {
    if (open && prePopulatedCustomer) {
      // Find the customer in mockCustomers by ID or create a customer object
      const customer = mockCustomers.find(c => c.id === prePopulatedCustomer.customerId);
      
      if (customer) {
        // Use existing customer from mock data
        setSelectedCustomer(customer);
        setFormData(prev => ({
          ...prev,
          customerId: customer.id,
          customerName: customer.name,
          customerAccountNumber: customer.accountNumber,
        }));
      } else {
        // Create a customer object from pre-populated data
        const newCustomer = {
          id: prePopulatedCustomer.customerId,
          name: prePopulatedCustomer.customerName,
          accountNumber: prePopulatedCustomer.accountNumber,
          email: '',
          phone: '',
        };
        setSelectedCustomer(newCustomer);
        setFormData(prev => ({
          ...prev,
          customerId: newCustomer.id,
          customerName: newCustomer.name,
          customerAccountNumber: newCustomer.accountNumber,
        }));
      }
    }
    
    // Auto-select pre-populated transactions
    if (open && prePopulatedTransactions && prePopulatedTransactions.length > 0) {
      const selectedIds = new Set(prePopulatedTransactions.map(t => t.id));
      const allocations: Record<string, number> = {};
      
      prePopulatedTransactions.forEach(transaction => {
        allocations[transaction.id] = transaction.balanceRemaining;
      });
      
      // Calculate total allocated amount
      const totalAllocated = Object.values(allocations).reduce((sum, amount) => sum + amount, 0);
      
      setFormData(prev => ({
        ...prev,
        selectedInvoices: selectedIds,
        invoiceAllocations: allocations,
        promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
      }));
    }
  }, [open, prePopulatedCustomer, prePopulatedTransactions]);

  // Reset form when drawer closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form
      setActiveTab('overview');
      setSelectedCustomer(null);
      setAvailableContacts([]);
      setSelectedContact(null);
      setFormData({
        customerId: '',
        customerName: '',
        customerAccountNumber: '',
        contactId: '',
        contactName: '',
        customerEmail: '',
        customerPhone: '',
        promiseDate: undefined,
        promisedAmount: '',
        confirmationMethod: '',
        selectedInvoices: new Set(),
        invoiceAllocations: {},
        notes: '',
        sendConfirmation: true,
      });
    }
    onOpenChange(newOpen);
  };

  // Handle customer selection
  const handleSelectCustomer = (customerId: string) => {
    const customer = mockCustomers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name,
        customerAccountNumber: customer.accountNumber,
        // Reset contact fields - will be populated by useEffect
        contactId: '',
        contactName: '',
        customerEmail: '',
        customerPhone: '',
      }));
    }
  };

  // Handle contact selection
  const handleSelectContact = (contactId: string) => {
    const contact = availableContacts.find(c => c.id === contactId);
    if (contact) {
      setSelectedContact(contact);
      setFormData(prev => ({
        ...prev,
        contactId: contact.id,
        contactName: `${contact.firstName} ${contact.surname}`,
        customerEmail: contact.email,
        customerPhone: contact.phone,
      }));
    }
  };

  // Handle invoice selection
  const handleToggleInvoice = (invoiceId: string, checked: boolean) => {
    const newSelected = new Set(formData.selectedInvoices);
    const newAllocations = { ...formData.invoiceAllocations };
    
    if (checked) {
      newSelected.add(invoiceId);
      const invoice = availableInvoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        newAllocations[invoiceId] = invoice.balanceRemaining;
      }
    } else {
      newSelected.delete(invoiceId);
      delete newAllocations[invoiceId];
    }
    
    // Calculate total allocated amount
    const totalAllocated = Object.values(newAllocations).reduce((sum, amount) => sum + amount, 0);
    
    setFormData({
      ...formData,
      selectedInvoices: newSelected,
      invoiceAllocations: newAllocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    });
  };

  // Handle allocation amount change
  const handleAllocationChange = (invoiceId: string, amount: number) => {
    const newAllocations = {
      ...formData.invoiceAllocations,
      [invoiceId]: amount,
    };
    
    // Calculate total allocated amount
    const totalAllocated = Object.values(newAllocations).reduce((sum, amt) => sum + amt, 0);
    
    setFormData({
      ...formData,
      invoiceAllocations: newAllocations,
      promisedAmount: totalAllocated > 0 ? totalAllocated.toFixed(2) : '',
    });
  };

  // Calculate total promised amount from allocations
  const calculateTotalFromAllocations = () => {
    return Object.values(formData.invoiceAllocations).reduce((sum, amount) => sum + amount, 0);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!selectedCustomer) {
      return 'Please select a customer';
    }
    
    if (!selectedContact) {
      return 'Please select a contact';
    }
    
    if (!formData.promiseDate) {
      return 'Please select a promise date';
    }
    
    if (!formData.promisedAmount || parseFloat(formData.promisedAmount) <= 0) {
      return 'Please enter a valid promised amount';
    }
    
    if (!formData.confirmationMethod) {
      return 'Please select a confirmation method';
    }
    
    if (formData.selectedInvoices.size === 0) {
      return 'Please select at least one invoice';
    }
    
    const totalAllocated = calculateTotalFromAllocations();
    const promisedAmount = parseFloat(formData.promisedAmount);
    
    if (Math.abs(totalAllocated - promisedAmount) > 0.01) {
      return `Invoice allocations (${totalAllocated.toFixed(2)}) must equal promised amount (${promisedAmount.toFixed(2)})`;
    }
    
    return null;
  };

  // Handle save
  const handleSave = () => {
    const validationError = validateForm();
    
    if (validationError) {
      toast.error(validationError);
      return;
    }

    // After validation, selectedCustomer and promiseDate are guaranteed to be defined
    const data: PromiseFormData = {
      customer: {
        id: selectedCustomer!.id,
        name: selectedCustomer!.name,
        accountNumber: selectedCustomer!.accountNumber,
        email: selectedCustomer!.email,
        phone: selectedCustomer!.phone,
      },
      promiseDate: formData.promiseDate!,
      promisedAmount: parseFloat(formData.promisedAmount),
      confirmationMethod: formData.confirmationMethod,
      selectedInvoices: Array.from(formData.selectedInvoices),
      invoiceAllocations: formData.invoiceAllocations,
      notes: formData.notes,
      sendConfirmation: formData.sendConfirmation,
    };

    if (onSave) {
      onSave(data);
    }

    toast.success('Promise to pay created successfully');
    handleOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    handleOpenChange(false);
  };

  // Calculate totals
  const totalInvoiceAmount = Array.from(formData.selectedInvoices)
    .map(id => mockInvoices.find(inv => inv.id === id))
    .filter(Boolean)
    .reduce((sum, inv) => sum + (inv?.balanceRemaining || 0), 0);

  const totalAllocated = calculateTotalFromAllocations();

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-[1100px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="flex items-center gap-2 text-2xl">
                <HandCoins className="h-6 w-6 text-primary" />
                Create New Promise
              </SheetTitle>
              <SheetDescription className="mt-1">
                Record a new payment commitment from a customer
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="h-4 w-4 mr-2" />
                Create Promise
              </Button>
            </div>
          </div>

          {/* Progress Indicator */}
          {selectedCustomer && (
            <div className="mt-4">
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
                <User className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">Customer:</span>
                <span className="font-medium text-primary">{selectedCustomer.name}</span>
                <span className="text-sm text-muted-foreground">({selectedCustomer.accountNumber})</span>
              </div>
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4 border-b shrink-0">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="transactions" className="flex items-center gap-2">
                Transactions
                {formData.selectedInvoices.size > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                    {formData.selectedInvoices.size}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="activity">
                Activity
              </TabsTrigger>
              <TabsTrigger value="audit">
                Audit Log
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 h-full">
            <ScrollAreaViewport className="h-full w-full">
            {/* Overview Tab */}
            <TabsContent value="overview" className="px-6 py-4 space-y-6">
              {/* Two-Column Layout: Customer Information (Left) and Promise Details (Right) */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Customer Information Panel - LEFT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Select the customer and contact making the promise</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Customer Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        Customer *
                      </Label>
                      {prePopulatedCustomer ? (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">{formData.customerName} ({formData.customerAccountNumber})</p>
                          <Badge variant="secondary" className="text-xs">Pre-selected</Badge>
                        </div>
                      ) : (
                        <Select
                          value={formData.customerId}
                          onValueChange={handleSelectCustomer}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select a customer" />
                          </SelectTrigger>
                          <SelectContent>
                            {mockCustomers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                {customer.name} ({customer.accountNumber})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Contact Name Dropdown */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Contact Name *
                      </Label>
                      <Select
                        value={formData.contactId}
                        onValueChange={handleSelectContact}
                        disabled={!selectedCustomer || availableContacts.length === 0}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder={
                            !selectedCustomer 
                              ? "Select a customer first" 
                              : availableContacts.length === 0 
                                ? "No contacts available" 
                                : "Select a contact"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {availableContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.surname}
                              {contact.isPrimary && " (Primary)"}
                              {contact.jobTitle && ` - ${contact.jobTitle}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Email Address (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Email Address
                      </Label>
                      <Input
                        value={formData.customerEmail}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view email"
                      />
                    </div>

                    {/* Phone Number (Auto-populated) */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        Phone Number
                      </Label>
                      <Input
                        value={formData.customerPhone}
                        readOnly
                        disabled
                        className="h-9 bg-muted"
                        placeholder="Select a contact to view phone"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Promise Details Panel - RIGHT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Promise Details
                    </CardTitle>
                    <CardDescription>Enter the payment commitment details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                      {/* Promise Date - Left column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Promise Date *
                        </Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full justify-start text-left font-normal h-9 text-sm',
                                !formData.promiseDate && 'text-muted-foreground'
                              )}
                            >
                              <Calendar className="mr-2 h-3 w-3" />
                              {formData.promiseDate ? format(formData.promiseDate, 'dd/MM/yyyy') : 'Pick date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent
                              mode="single"
                              selected={formData.promiseDate}
                              onSelect={(date) => setFormData({ ...formData, promiseDate: date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Promised Amount - Right column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <PoundSterling className="h-3 w-3" />
                          Promised Amount *
                        </Label>
                        <Input
                          type="text"
                          placeholder="0.00"
                          value={formData.promisedAmount ? formatCurrency(parseFloat(formData.promisedAmount)) : ''}
                          readOnly
                          disabled
                          className="h-9 bg-muted font-medium"
                          title="This amount is automatically calculated from selected transactions"
                        />
                        <p className="text-xs text-muted-foreground">
                          Auto-calculated from transactions
                        </p>
                      </div>

                      {/* Confirmation Method - Left column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Confirmation Method *
                        </Label>
                        <Select
                          value={formData.confirmationMethod}
                          onValueChange={(value) => setFormData({ ...formData, confirmationMethod: value })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Phone">Phone Call</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Portal">Customer Portal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Related Collection Case - Right column */}
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Folder className="h-3 w-3" />
                          Collection Case (Optional)
                        </Label>
                        <Select
                          value={formData.caseId || 'none'}
                          onValueChange={(value) => setFormData({ ...formData, caseId: value === 'none' ? undefined : value })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {mockCases
                              .filter(c => c.status === 'Active')
                              .map((caseItem) => (
                                <SelectItem key={caseItem.id} value={caseItem.id}>
                                  {caseItem.caseNumber}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Summary Card - Full Width Below */}
              {selectedCustomer && formData.promiseDate && formData.promisedAmount && formData.confirmationMethod && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5 text-primary" />
                      Promise Summary
                    </CardTitle>
                    <CardDescription>Review the promise details before saving</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer</p>
                        <p className="text-sm font-semibold">{selectedCustomer.name}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Account Number</p>
                        <p className="text-sm font-mono font-semibold">{selectedCustomer.accountNumber}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Promise Date</p>
                        <p className="text-sm font-semibold">{format(formData.promiseDate, 'MMM dd, yyyy')}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Promised Amount</p>
                        <p className="text-sm font-bold text-primary">
                          {formatCurrency(parseFloat(formData.promisedAmount))}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Confirmation Method</p>
                        <Badge variant="outline">{formData.confirmationMethod}</Badge>
                      </div>
                      {formData.caseId && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground">Collection Case</p>
                          <p className="text-sm font-mono font-semibold">
                            {mockCases.find(c => c.id === formData.caseId)?.caseNumber}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Promise Notes Panel - Full Width at Bottom */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Promise Notes
                  </CardTitle>
                  <CardDescription>Add any additional information about this promise</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={6}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    These notes will be visible in the promise details and activity history
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Outstanding Transactions
                  </CardTitle>
                  <CardDescription>
                    Select invoices to include in this promise and allocate payment amounts ({mockInvoices.length} available)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Reference Number</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                          <TableHead className="text-right">Allocated Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableInvoices.map((invoice) => {
                          const isSelected = formData.selectedInvoices.has(invoice.id);
                          const allocatedAmount = formData.invoiceAllocations[invoice.id] || 0;
                          const dueDate = new Date(invoice.invoiceDate.getTime() + 30 * 24 * 60 * 60 * 1000);

                          return (
                            <TableRow key={invoice.id}>
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) =>
                                    handleToggleInvoice(invoice.id, checked as boolean)
                                  }
                                />
                              </TableCell>
                              <TableCell className="font-medium">
                                {invoice.headerReference}
                              </TableCell>
                              <TableCell>{format(invoice.invoiceDate, 'dd/MM/yyyy')}</TableCell>
                              <TableCell>{format(dueDate, 'dd/MM/yyyy')}</TableCell>
                              <TableCell>
                                <TransactionTypeBadge type="invoice" />
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(invoice.originalAmount)}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(invoice.balanceRemaining)}
                              </TableCell>
                              <TableCell>
                                <Badge variant="destructive">Unpaid</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                {invoice.daysOverdue > 0 ? (
                                  <span className="text-red-600 dark:text-red-400 font-medium">
                                    {invoice.daysOverdue}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {isSelected ? (
                                  <Input
                                    type="number"
                                    value={allocatedAmount.toFixed(2)}
                                    onChange={(e) =>
                                      handleAllocationChange(invoice.id, parseFloat(e.target.value) || 0)
                                    }
                                    className="w-32 text-right h-8"
                                    min="0"
                                    max={invoice.balanceRemaining}
                                    step="0.01"
                                  />
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(availableInvoices.reduce((sum, inv) => sum + inv.balanceRemaining, 0))}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Allocation Summary */}
              {formData.selectedInvoices.size > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Allocation Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Selected Invoices</span>
                      <span className="font-medium">{formData.selectedInvoices.size}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Invoice Balance</span>
                      <span className="font-medium">{formatCurrency(totalInvoiceAmount)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Allocated</span>
                      <span className="font-medium text-lg">{formatCurrency(totalAllocated)}</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Promised Amount</span>
                      <span
                        className={cn(
                          'font-medium text-lg',
                          formData.promisedAmount &&
                            Math.abs(totalAllocated - parseFloat(formData.promisedAmount)) < 0.01
                            ? 'text-green-600'
                            : 'text-amber-600'
                        )}
                      >
                        {formData.promisedAmount ? formatCurrency(parseFloat(formData.promisedAmount)) : formatCurrency(0)}
                      </span>
                    </div>
                    {formData.promisedAmount &&
                      Math.abs(totalAllocated - parseFloat(formData.promisedAmount)) > 0.01 && (
                        <>
                          <Separator />
                          <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg">
                            <MessageSquare className="h-4 w-4" />
                            <span>
                              Allocations must equal promised amount. Difference: $
                              {Math.abs(totalAllocated - parseFloat(formData.promisedAmount)).toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Activity Tab - Consolidated view of Activity, Notes, and Communications */}
            <TabsContent value="activity" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">Activity Timeline</CardTitle>
                      <CardDescription>
                        Add notes and view activity history for this promise
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add New Note Section */}
                  <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
                    <Label htmlFor="promiseNotes" className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Promise Notes
                    </Label>
                    <Textarea
                      id="promiseNotes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Enter notes about the promise, customer conversation, or any special conditions..."
                      rows={6}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      These notes will be visible in the promise details and activity history
                    </p>
                  </div>

                  {/* Note Preview */}
                  {formData.notes && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Note Preview
                        </Label>
                        <div className="bg-muted p-4 rounded-lg border">
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">{formData.notes}</p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Information Banner */}
                  <Separator />
                  <div className="flex items-start gap-3 p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Activity History
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Once this promise is created, all activities, notes, and communications will be tracked and displayed here in a unified timeline. This includes emails sent, calls logged, status changes, and any notes added by team members.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Log Tab */}
            <TabsContent value="audit" className="px-6 py-4 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>
                    Complete chronological history of all promise modifications and changes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-base font-medium mb-2">No Audit History Yet</p>
                    <p className="text-sm max-w-md mx-auto">
                      Once this promise is created, all modifications and changes will be tracked here. This includes status changes, amount updates, date modifications, and field edits.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" className="z-10" />
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};
